import { useCallback, useRef } from 'react'

import { getUrl } from 'app/UrlService'
import { useAppUserContext } from 'app/useAppUserContext'
import { useObject } from 'data/hooks/objects'
import { useViews } from 'data/hooks/views'
import useEditMode from 'features/admin/edit-mode/useEditMode'
import { CreateViewModalHandle } from 'features/views/common/CreateViewModal'

import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

import { ColorScheme } from 'ui/styling/baseVariables/colors/colorPalette'

import { useToolbarContext } from './useToolbarContext'

const VIEW_TYPES = ['list', 'detail', 'create'] as const
const VIEW_TYPES_TO_DISPLAY = new Set<ViewDto['type']>(VIEW_TYPES)

const VIEW_TYPE_SORT_ORDER = {
    list: 0,
    detail: 1,
    create: 2,
} as const

type ViewTypeAttributes = {
    label: string
    value: ViewDto['type']
    color: ColorScheme
}

const VIEW_TYPE_ATTRIBUTES: Record<(typeof VIEW_TYPES)[number], ViewTypeAttributes> = {
    list: {
        label: 'List view',
        value: 'list',
        color: 'Indigo',
    },
    detail: {
        label: 'Detail page',
        value: 'detail',
        color: 'Rose',
    },
    create: {
        label: 'Create form',
        value: 'create',
        color: 'Emerald',
    },
} as const

type ViewOption = {
    label: string
    typeAttrs: ViewTypeAttributes
    url: string
}

export function useToolbarActionsViewsState() {
    const { objectSid, records } = useToolbarContext()

    const { object } = useObject(objectSid)
    const { data: views, isError } = useViews()

    const isEmpty = views?.length === 0 && !isError
    const isLoading = (!views || !object) && !isError

    const firstRecord = records[0]

    const viewsToDisplay =
        views?.reduce((acc, view) => {
            if (view.object_id !== objectSid) return acc
            if (!VIEW_TYPES_TO_DISPLAY.has(view.type)) return acc

            const typeAttrs = VIEW_TYPE_ATTRIBUTES[view.type as keyof typeof VIEW_TYPE_ATTRIBUTES]

            // We can't access detail page for an object with no records.
            let url = getUrl(view.url)
            if (view.type === 'detail') {
                url = firstRecord && object ? getUrl(`${object.url}/view/${firstRecord._sid}`) : ''
            }

            acc.push({
                label: view.name ?? '',
                typeAttrs,
                url,
            })

            return acc
        }, [] as ViewOption[]) ?? []
    viewsToDisplay.sort((a, b) => {
        // Enforce stable sort order for views.
        const aOrder = VIEW_TYPE_SORT_ORDER[a.typeAttrs.value as keyof typeof VIEW_TYPE_SORT_ORDER]
        const bOrder = VIEW_TYPE_SORT_ORDER[b.typeAttrs.value as keyof typeof VIEW_TYPE_SORT_ORDER]

        return aOrder - bOrder
    })

    const { open: openEditMode } = useEditMode()
    const onViewSelected = useCallback(() => {
        // Wait for route change to complete before opening edit mode.
        setTimeout(() => {
            openEditMode()
        }, 300)
    }, [openEditMode])

    const { isAdmin } = useAppUserContext()
    const allowAddNewView = isAdmin

    const createViewModalRef = useRef<CreateViewModalHandle>(null)
    const addNewView = useCallback((display: ListViewDisplay) => {
        requestAnimationFrame(() => {
            createViewModalRef.current?.open(display)
        })
    }, [])

    const onAddNewView = useCallback(() => {
        onViewSelected()
    }, [onViewSelected])

    return useDeepEqualsMemoValue({
        views: viewsToDisplay,
        isEmpty,
        isLoading,
        isError,
        onViewSelected,
        createViewModalRef,
        allowAddNewView,
        addNewView,
        onAddNewView,
    })
}
